/* eslint-disable camelcase */
import { useState, useEffect } from 'react';
import moment from 'moment';
import getUrlWithCacheBusterFromJwt from '../../utils/methods/getUrlWithCacheBusterFromJwt';
import { PPMSSnippetCodeTemplate } from './PPMSSnippetCodeTemplate';
import getJwtPayload from '../../utils/methods/getJwtPayload/getJwtPayload';
import useDataLayer from '../../utils/methods/useDataLayer';

class RequestAttemptFailed extends Error {
  constructor() {
    super('Request attempt failed');
  }
}

function getRequestConfig({ jwt, publicIntegration }) {
  const headers = {
    'Content-Type': 'application/vnd.api+json',
  };

  if (!publicIntegration) {
    headers.Authorization = `Bearer ${jwt}`;
  }

  return {
    method: 'GET',
    headers,
  };
}

function wait(time) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, time);
  });
}

function retry(next, { limit, interval } = { limit: 5, interval: 5000 }) {
  let attemptsLeft = limit;

  const attempt = () => next().catch((err) => {
    if (err instanceof RequestAttemptFailed && attemptsLeft) {
      attemptsLeft--;

      return wait(interval).then(() => next());
    }

    throw err;
  });

  return attempt().catch(attempt);
}

function useIntegration({ jwt, initial, publicIntegration }) {
  const [data, setData] = useState(initial);
  const url = '/api/organization/v2/product-analytics';

  useEffect(() => {
    if (!initial) {
      retry(() => fetch(
        publicIntegration
          ? url
          : getUrlWithCacheBusterFromJwt(url, jwt),
        getRequestConfig({ jwt, publicIntegration }),
      )
        .then((resp) => {
          if (!resp.ok) return Promise.reject(new RequestAttemptFailed());

          return resp.json();
        })
        .then((resp) => {
          setData(resp);
        })).catch(() => {});
    }
  }, [initial]);

  return data;
}

function useIntercomIntegration({ jwt, productAnalyticsEnabled }) {
  const [data, setData] = useState({ hash: null });
  const url = '/api/organization/v1/integrations/intercom';

  useEffect(() => {
    if (productAnalyticsEnabled && jwt) {
      retry(() => fetch(
        getUrlWithCacheBusterFromJwt(url, jwt),
        getRequestConfig({ jwt }),
      )
        .then((resp) => {
          if (!resp.ok) return Promise.reject(new RequestAttemptFailed());

          return resp.json();
        })
        .then((resp) => {
          setData(resp);
        })).catch(() => {});
    }
  }, [productAnalyticsEnabled]);

  return data;
}

function useTrafficUsage({ url, jwt, initial, publicIntegration, userRole }) {
  const [data, setData] = useState(initial);

  useEffect(() => {
    if (!initial && !publicIntegration && userRole === 'OWNER') {
      retry(() => fetch(getUrlWithCacheBusterFromJwt(url, jwt), getRequestConfig({ jwt }))
        .then((resp) => {
          if (!resp.ok) return Promise.reject(new RequestAttemptFailed());

          return resp.json();
        })
        .then((resp) => {
          setData(resp.data);
        })).catch(() => {});
    }
  }, [initial, publicIntegration, userRole]);

  return data;
}

function useLicense({ url, jwt, initial, publicIntegration }) {
  const [data, setData] = useState(initial);

  useEffect(() => {
    if (!initial && !publicIntegration) {
      retry(() => fetch(getUrlWithCacheBusterFromJwt(url, jwt), getRequestConfig({ jwt }))
        .then((resp) => {
          if (!resp.ok) return Promise.reject(new RequestAttemptFailed());

          return resp.json();
        })
        .then((resp) => {
          setData(resp.data);
        })).catch(() => {});
    }
  }, [initial, publicIntegration]);

  return data;
}

function getNonceToken() {
  const scripts = document.querySelectorAll('script');
  const scriptWithNonce = Array.from(scripts).find(script => !!script.nonce);
  if (scriptWithNonce) {
    return scriptWithNonce.nonce;
  }

  return null;
}

function injectSnippet(domain, appId, nonceToken) {
  const script = document.createElement('script');
  const bodyFirstChild = document.body.firstChild;

  if (nonceToken) {
    script.nonce = nonceToken;
  }

  script.innerHTML = PPMSSnippetCodeTemplate(domain, appId);
  bodyFirstChild.parentNode.insertBefore(script, bodyFirstChild);
}

function PPMSSnippet({
  jwt,
  trafficMonitorApiUrl,
  licenseApiUrl,
  license,
  traffic,
  integration,
  publicIntegration,
  productAnalyticsAppIdFieldName,
  userRole,
} = {}) {
  const [isSnippetAppended, setSnippetStatus] = useState(false);
  const dataLayer = useDataLayer();
  const trafficData = useTrafficUsage({ jwt, url: trafficMonitorApiUrl, initial: traffic, publicIntegration, userRole });
  const integrationData = useIntegration({ jwt, publicIntegration, initial: integration });
  const licenseData = useLicense({ jwt, url: licenseApiUrl, initial: license, publicIntegration });
  const intercomUserHash = useIntercomIntegration({ jwt, productAnalyticsEnabled: integrationData?.enabled }).hash;

  useEffect(() => {
    if (licenseData) {
      const {
        plan: {
          type: licensePlan,
          traffic_limit: trafficLimit,
        },
        expires_at: licenseExpiresAt,
        modules,
      } = licenseData.attributes;

      dataLayer.push({
        licensePlan,
        trafficLimit,
        licenseExpiresAt,
        modules,
      });
    }
  }, [licenseData]);

  useEffect(() => {
    if (trafficData) {
      const trafficPreviousMonth = trafficData[0].total_traffic;
      const trafficCurrentMonth = trafficData[1].total_traffic;

      dataLayer.push({
        trafficCurrentMonth,
        trafficPreviousMonth,
      });
    }
  }, [trafficData]);

  useEffect(() => {
    if (integrationData) {
      const nonceToken = getNonceToken();
      const integrationPayload = {
        dataLayerValues: integrationData.data_layer[0],
        ppmsDomain: integrationData.domain,
        ppmsAppId: integrationData[productAnalyticsAppIdFieldName],
        enabled: integrationData.enabled,
      };

      if (!publicIntegration) {
        const jwtPayload = getJwtPayload(jwt);
        integrationPayload.dataLayerValues = {
          ...integrationPayload.dataLayerValues,
          role: userRole,
          email: jwtPayload.eml,
          user_id: jwtPayload.sub,
        };
      }

      const { dataLayerValues, ppmsDomain, ppmsAppId, enabled } = integrationPayload;

      if (!isSnippetAppended && enabled) {
        // Append snippet into start of the body tag
        injectSnippet(ppmsDomain, ppmsAppId, nonceToken);
        setSnippetStatus(true);
      }
      dataLayer.push(dataLayerValues);
    }
  }, [integrationData]);

  useEffect(() => {
    if (intercomUserHash) {
      dataLayer.push({
        event: '@intercom/user/hash',
        intercom_user_hash: intercomUserHash,
      });
    }
  }, [integrationData, intercomUserHash]);

  return null;
}

const previousMonth = moment(new Date()).subtract(1, 'months').format('YYYY-MM');
const currentMonth = moment(new Date()).format('YYYY-MM');

PPMSSnippet.defaultProps = {
  jwt: undefined,
  trafficMonitorApiUrl: `/api/traffic-monitor/v2/usage?from=${previousMonth}&to=${currentMonth}`,
  licenseApiUrl: '/api/license-keeper/v1/license',
  publicIntegration: false,
  userRole: 'OWNER',
  productAnalyticsAppIdFieldName: 'app_id',
};

export default PPMSSnippet;

export {
  useIntegration,
  useTrafficUsage,
  useLicense,
};
